import { HttpClient } from "@angular/common/http";
import { effect, inject, Injectable } from "@angular/core";
import { CacheService } from "app/core/services/cache.service";
import { LanguageService } from "app/core/services/language.service";
import { LoaderService } from "app/shared/components/loader/loader.service";
import { PermissionTypes } from "app/shared/enums/permission-types.enum";
import { UserInfo } from "app/shared/models/user-info.interface";
import { UserPermissions } from "app/shared/models/user-permissions.type";
import { environment } from "environments/environment";
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  ReadyArgs,
  typeEventArgs,
} from "keycloak-angular";
import Keycloak from "keycloak-js";
import { BehaviorSubject, filter, map, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private readonly keycloak = inject(Keycloak);
  private languageService = inject(LanguageService);
  private http = inject(HttpClient);
  private loaderService = inject(LoaderService);

  private readonly USER_URL = `${environment.API_URL}/user`;
  private _keycloakReady$ = new BehaviorSubject<boolean>(false);

  private _accessToken$ = new BehaviorSubject<string | undefined>(undefined);
  private _userInfo$ = new BehaviorSubject<UserInfo | null>(null);
  private _userPermissions$ = new BehaviorSubject<UserPermissions[] | null>(
    null,
  );

  public hasManagementPermissions$!: Observable<boolean>;
  public userInfo$ = this._userInfo$.asObservable().pipe(filter(Boolean));
  public userPermissions$ = this._userPermissions$.asObservable();
  public tokenReady$: Observable<boolean>;
  public permissionsReady$: Observable<boolean>;

  constructor() {
    const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

    effect(() => {
      const keycloakEvent = keycloakSignal();

      if (keycloakEvent.type === KeycloakEventType.Ready) {
        this._keycloakReady$.next(true);
        this.emitToken();
        this.fetchUserPermissions();
        this.fetchUserInfo();
      }

      if (keycloakEvent?.type === KeycloakEventType.AuthRefreshSuccess) {
        this.emitToken();
        this.fetchUserPermissions();
        this.fetchUserInfo();
      }

      if (keycloakEvent?.type === KeycloakEventType.AuthRefreshError) {
        this.cleanAuthValues();
      }

      if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
        this.cleanAuthValues();
      }
    });

    this.hasManagementPermissions$ = this.userPermissions$.pipe(
      map((perms) => this.canManageUsers(perms || [])),
    );

    this.tokenReady$ = this.accessToken$.pipe(
      filter(() => !!this.keycloak.tokenParsed),
      map((value) => (value ? true : false)),
    );

    this.permissionsReady$ = this.userPermissions$.pipe(
      filter((perms) => perms !== null),
      map(() => true),
    );
  }

  get accessToken$(): Observable<string | undefined> {
    return this._accessToken$
      .asObservable()
      .pipe(filter((tok) => tok != null) as any);
  }

  waitForKeycloakReady(): Observable<boolean> {
    return this._keycloakReady$.pipe(
      filter((ready) => ready),
      map(() => this.keycloak.authenticated || false),
    );
  }

  login(): void {
    this.keycloak.login({
      redirectUri: window.location.origin + "/auth/callback",
    });
  }

  logout(clearStorage = false): void {
    // Clear any stored redirect
    sessionStorage.removeItem("auth_redirect");

    // Clear local storage items that might interfere
    localStorage.removeItem("kc-callback-" + this.keycloak.clientId);

    // Logout from Keycloak with redirect to root
    this.keycloak.logout({
      redirectUri: window.location.origin + "/",
    });

    if (clearStorage) {
      CacheService.clearSessionStorage();
    }

    this.cleanAuthValues();
    this.loaderService.show();
  }

  getAccessToken(): string | undefined {
    return this.keycloak.token;
  }

  isTokenValid(): boolean {
    return !this.keycloak.isTokenExpired(-1);
  }

  async updateToken(minValidity = 10): Promise<boolean> {
    try {
      return await this.keycloak.updateToken(minValidity);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Token refresh failed", err);
      return false;
    }
  }

  canManageUsers(userPermissions: UserPermissions[]): boolean {
    if (!userPermissions?.length) {
      return false;
    }

    let permissions: string[] = userPermissions.flatMap(
      ({ permissions }) => permissions,
    );
    permissions = Array.from(new Set(permissions));

    return permissions.some((permission) =>
      (Object.values(PermissionTypes) as string[])
        .filter((p) => p !== PermissionTypes.User)
        .includes(permission),
    );
  }

  private emitToken(): void {
    this._accessToken$.next(this.keycloak.token);
    this.cleanKeycloakStaleStorage();
  }

  private cleanKeycloakStaleStorage(): void {
    Object.keys(localStorage)
      .filter((key) => key.startsWith("kc-callback-"))
      .forEach((key) => localStorage.removeItem(key));
  }

  private cleanAuthValues(): void {
    this._userInfo$.next(null);
    this._userPermissions$.next(null);
  }

  private updateLanguageFromToken(): void {
    const locale = this.keycloak.tokenParsed?.locale as string | undefined;
    if (locale) {
      this.languageService.updateLanguage(locale);
    }
  }

  private fetchUserInfo(): void {
    const parsed = this.keycloak.tokenParsed;
    if (!parsed) return;

    this.http.get<UserInfo>(`${this.USER_URL}/info`).subscribe({
      next: (info) => {
        if (info.user.language) {
          this.languageService.updateLanguage(info.user.language);
        } else {
          this.updateLanguageFromToken();
        }

        this._userInfo$.next(info);
      },
      error: (err) => {
        this._userInfo$.next(null);
        // eslint-disable-next-line no-console
        console.error("[AuthService] Failed to fetch user info", err);
      },
    });
  }

  private fetchUserPermissions(): void {
    const parsed = this.keycloak.tokenParsed;
    if (!parsed) return;

    this.http.get<UserPermissions[]>(`${this.USER_URL}/asperus`).subscribe({
      next: (perms) => this._userPermissions$.next(perms),
      error: (err) => {
        this._userPermissions$.next([]);
        // eslint-disable-next-line no-console
        console.error("[AuthService] Failed to fetch user permissions", err);
      },
    });
  }
}
